import { EntityKindHandler, type SplitEntities, type EntityMenuContext, type EntityInteractionContext, type PointerDownGuardResult } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition } from '../../entityTypes';
import { getConnectorData } from '../../entityTypes';
import { CanvasWaypointNode } from './WaypointNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Trash2 } from 'lucide-react';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { WAYPOINT_RADIUS } from '../../types';
import i18n from '../../../i18n';

export class WaypointHandler extends EntityKindHandler {
  readonly kind = 'waypoint';
  readonly Component = CanvasWaypointNode;
  readonly dataAttribute = 'data-canvas-waypoint';
  canBoxSelect = true;
  canConnect = true;

  getDomPosition(entity: CanvasEntity): { left: number; top: number } {
    return { left: entity.x - WAYPOINT_RADIUS, top: entity.y - WAYPOINT_RADIUS };
  }

  getConnectorPositions(entity: CanvasEntity): ConnectorPosition[] {
    return [{ side: 'center', x: entity.x, y: entity.y }];
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity } = ctx;
    const waypointId = entity.id;
    return [{
      id: 'delete-wp',
      label: i18n.t('canvasEntity.deleteWaypoint' as any),
      icon: Trash2,
      iconColor: '#dc2626',
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: waypointId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    }];
  }

  onPointerDownGuard(ctx: EntityInteractionContext, e: React.PointerEvent): PointerDownGuardResult | null {
    // Detect outer half for starting a connection
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const clickDist = Math.hypot(e.clientX - cx, e.clientY - cy);
    const isOuterHalf = clickDist > WAYPOINT_RADIUS * 0.5;
    if (isOuterHalf) {
      ctx.handleWaypointConnectorMouseDown(ctx.entity.id, e);
      return { handled: true };
    }
    return null;
  }

  onRemove(entityId: string, split: SplitEntities): SplitEntities {
    // When removing a waypoint, merge the two connectors passing through it
    const connectedFrom: CanvasEntity[] = []; // connectors where from = this waypoint
    const connectedTo: CanvasEntity[] = [];   // connectors where to = this waypoint
    const unrelatedConnectors: Record<string, CanvasEntity> = {};

    for (const e of Object.values(split.connectors)) {
      const d = getConnectorData(e);
      const isFrom = d.from.entityId === entityId;
      const isTo = d.to.entityId === entityId;
      if (isFrom && isTo) continue; // self-loop, just remove
      if (isFrom) { connectedFrom.push(e); continue; }
      if (isTo) { connectedTo.push(e); continue; }
      unrelatedConnectors[e.id] = e;
    }

    // Merge: for each pair (incoming -> waypoint, waypoint -> outgoing), create a direct connector
    const merged: Record<string, CanvasEntity> = {};
    const usedFrom = new Set<string>();
    const usedTo = new Set<string>();

    const { [entityId]: _, ...restNodes } = split.nodes;
    const allOther = [...Object.values(restNodes), ...Object.values(unrelatedConnectors)];

    for (const incoming of connectedTo) {
      for (const outgoing of connectedFrom) {
        if (usedFrom.has(outgoing.id) || usedTo.has(incoming.id)) continue;
        const inData = getConnectorData(incoming);
        const outData = getConnectorData(outgoing);

        if (inData.from.entityId === outData.to.entityId && inData.from.side === outData.to.side) continue;

        const mergedId = `conn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const maxZ = allOther.reduce((m, e) => Math.max(m, e.zIndex ?? 0), 0);
        const mergedEntity: CanvasEntity = {
          id: mergedId,
          kind: 'connector',
          x: 0, y: 0, width: 0, height: 0,
          layerId: '',
          connectable: false,
          zIndex: maxZ + Object.keys(merged).length + 1,
          data: {
            from: inData.from,
            to: outData.to,
            segmentType: inData.segmentType,
          },
        };
        merged[mergedId] = mergedEntity;
        usedTo.add(incoming.id);
        usedFrom.add(outgoing.id);
      }
    }

    return {
      nodes: restNodes,
      connectors: { ...unrelatedConnectors, ...merged },
      groups: split.groups,
    };
  }
}
